import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import { getPartnerAuthHeaders } from "@/lib/apiAuth";
import { toast } from "sonner";
import { Loader2, X } from "lucide-react";
import PartnerSelect from "@/pages/LandingPages/marketingAgency/shared/PartnerSelect";
import {
  AI_EMPLOYEE_CAPABILITY_OPTIONS,
  EXPECTED_DEPLOYS_90D_OPTIONS,
} from "@/constants/partnerAiEmployeeRequestOptions";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ALL_CAPABILITIES = AI_EMPLOYEE_CAPABILITY_OPTIONS.map((cap) => cap.value);

const INITIAL_FORM = {
  verticalName: "",
  exampleWebsiteUrl: "",
  expectedDeploys90d: "1",
  capabilities: ALL_CAPABILITIES,
  additionalNotes: "",
};

const buildSubmitPayload = (form) => {
  const verticalName = form.verticalName.trim();
  const capabilityLabels = form.capabilities
    .map((value) => AI_EMPLOYEE_CAPABILITY_OPTIONS.find((opt) => opt.value === value)?.label || value)
    .join(", ");

  return {
    vertical_name: verticalName,
    industry_category: "other",
    example_website_url: form.exampleWebsiteUrl.trim(),
    why_now: `Partner requested a new Digital Assistant: ${verticalName}.`,
    expected_deploys_90d: form.expectedDeploys90d,
    capabilities: form.capabilities,
    typical_conversation: `Standard workflow for ${verticalName} with capabilities: ${capabilityLabels}.`,
    additional_notes: form.additionalNotes.trim() || null,
  };
};

const extractErrorMessage = (err, fallback) => {
  if (err?.response?.data?.detail) {
    const detail = err.response.data.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail) && detail[0]?.msg) {
      const field = detail[0]?.loc?.slice(-1)[0];
      return field ? `${field}: ${detail[0].msg}` : detail[0].msg;
    }
  }
  if (err?.response?.data?.message) return err.response.data.message;
  if (err?.request && !err?.response) {
    return "Network error. Please check your connection and try again.";
  }
  return fallback;
};

const RequestAiEmployeeModal = ({ open, onClose, onSuccess }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState(INITIAL_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    if (open) {
      setForm({ ...INITIAL_FORM, capabilities: [...ALL_CAPABILITIES] });
      setSubmitError("");
    }
  }, [open]);

  if (!open) return null;

  const toggleCapability = (value) => {
    setForm((prev) => {
      const set = new Set(prev.capabilities);
      if (set.has(value)) set.delete(value);
      else set.add(value);
      return { ...prev, capabilities: [...set] };
    });
    setSubmitError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (form.capabilities.length === 0) {
      setSubmitError("Select at least one capability.");
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");

    const payload = buildSubmitPayload(form);

    try {
      await axios.post(`${BACKEND_URL}/api/partner/ai-employee-requests`, payload, {
        headers: getPartnerAuthHeaders(),
      });
      toast.success("Request submitted. We will review your request and follow up with a detailed feature breakdown.");
      onSuccess?.();
      onClose();
    } catch (err) {
      if (err?.response?.status === 401) {
        Cookies.remove("token");
        Cookies.remove("email");
        navigate("/login");
        return;
      }
      setSubmitError(
        extractErrorMessage(err, "Could not submit your request. Please try again.")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 overflow-y-auto"
      data-testid="request-ai-employee-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-2xl border border-partner bg-[#11151F] shadow-xl my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 sm:p-6 border-b border-partner">
          <div>
            <h2 className="text-lg font-bold text-partner">Request a new Digital Assistant</h2>
            <p className="text-sm text-partner-muted font-body mt-1">
              Tell us about the business type you need. We will review your request and share a
              detailed feature breakdown for this Digital Assistant. Once you confirm, our engineering
              team will build it.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-partner-muted hover:text-partner hover:bg-white/5"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 sm:p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          <section className="space-y-4">
            <h3 className="text-xs font-mono uppercase tracking-wider text-partner-accent">
              New Digital Assistant
            </h3>
            <div>
              <label className="block text-xs text-partner-muted font-body mb-1.5">
                Digital Assistant name *
              </label>
              <input
                type="text"
                value={form.verticalName}
                onChange={(e) => setForm((p) => ({ ...p, verticalName: e.target.value }))}
                className="w-full partner-input rounded-lg px-4 py-2.5 text-sm font-body"
                placeholder="HVAC, Dental intake, Law firm intake"
                required
                minLength={2}
                data-testid="request-ai-employee-name"
              />
            </div>
            <div>
              <label className="block text-xs text-partner-muted font-body mb-1.5">
                Example client website *
              </label>
              <input
                type="url"
                value={form.exampleWebsiteUrl}
                onChange={(e) => setForm((p) => ({ ...p, exampleWebsiteUrl: e.target.value }))}
                className="w-full partner-input rounded-lg px-4 py-2.5 text-sm font-body"
                placeholder="https://example-client.com"
                required
              />
            </div>
          </section>

          <section className="space-y-4">
            <h3 className="text-xs font-mono uppercase tracking-wider text-partner-accent">
              What it should do
            </h3>
            <div className="grid sm:grid-cols-2 gap-2">
              {AI_EMPLOYEE_CAPABILITY_OPTIONS.map((cap) => (
                <label
                  key={cap.value}
                  className="flex items-start gap-2 text-sm font-body text-partner cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={form.capabilities.includes(cap.value)}
                    onChange={() => toggleCapability(cap.value)}
                    className="mt-0.5 accent-[#00D2FF]"
                  />
                  <span>{cap.label}</span>
                </label>
              ))}
            </div>
          </section>

          <section className="space-y-4">
            <h3 className="text-xs font-mono uppercase tracking-wider text-partner-accent">
              Volume
            </h3>
            <div>
              <label className="block text-xs text-partner-muted font-body mb-1.5">
                Expected deploys in next 90 days *
              </label>
              <PartnerSelect
                value={form.expectedDeploys90d}
                onChange={(e) => setForm((p) => ({ ...p, expectedDeploys90d: e.target.value }))}
                options={EXPECTED_DEPLOYS_90D_OPTIONS}
              />
            </div>
            <div>
              <label className="block text-xs text-partner-muted font-body mb-1.5">
                Additional notes
              </label>
              <textarea
                value={form.additionalNotes}
                onChange={(e) => setForm((p) => ({ ...p, additionalNotes: e.target.value }))}
                className="w-full partner-input rounded-lg px-4 py-2.5 text-sm font-body min-h-[60px]"
                placeholder="CRM, scripts, compliance, or other context."
              />
            </div>
          </section>

          <p className="text-xs text-partner-muted font-body">
            You can deploy once the Digital Assistant is approved and enabled for your account.
          </p>

          {submitError && (
            <p className="text-sm text-red-400 font-body" data-testid="request-ai-employee-error">
              {submitError}
            </p>
          )}

          <div className="flex flex-col-reverse sm:flex-row gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2.5 rounded-lg border border-partner text-partner-muted font-body text-sm hover:text-partner hover:bg-[#080A0F] disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#00D2FF] text-[#080A0F] font-semibold text-sm hover:bg-[#00D2FF]/90 disabled:opacity-50"
              data-testid="request-ai-employee-submit"
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {isSubmitting ? "Submitting…" : "Submit request"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RequestAiEmployeeModal;
